import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react'
import { IAPIPrompt, IAPIFolder, IAPIPromptVariant, IAPIDeployment, IAPIDataset, IAPISimulationSuite } from 'extra/types/ApiModels'
import { api } from '../api/client'
import { useAuth } from './AuthContext'

interface EntitiesContextType {
  prompts: Array<IAPIPrompt>
  folders: Array<IAPIFolder>
  variants: Array<IAPIPromptVariant>
  datasets: Array<IAPIDataset>
  deployments: Array<IAPIDeployment>
  simulationSuites: Array<IAPISimulationSuite>
  loading: boolean
  refreshPrompts: () => Promise<void>
  refreshFolders: () => Promise<void>
  refreshVariants: () => Promise<void>
  refreshDatasets: () => Promise<void>
  refreshDeployments: () => Promise<void>
  refreshSimulationSuites: () => Promise<void>
  refreshAll: () => Promise<void>
}

const EntitiesContext = createContext<EntitiesContextType | undefined>(undefined)

export function EntitiesProvider ({ children }: { children: ReactNode }) {
  const [prompts, setPrompts] = useState<Array<IAPIPrompt>>([])
  const [folders, setFolders] = useState<Array<IAPIFolder>>([])
  const [variants, setVariants] = useState<Array<IAPIPromptVariant>>([])
  const [datasets, setDatasets] = useState<Array<IAPIDataset>>([])
  const [deployments, setDeployments] = useState<Array<IAPIDeployment>>([])
  const [simulationSuites, setSimulationSuites] = useState<Array<IAPISimulationSuite>>([])
  const [loading, setLoading] = useState(true)
  const { isAuthenticated } = useAuth()

  const fetchAll = useCallback(async () => {
    if (!isAuthenticated) return

    try {
      const [promptsData, foldersData, variantsData, datasetsData, deploymentsData, simulationSuitesData] = await Promise.all([
        api.prompts.list(),
        api.folders.list(),
        api.promptVariants.listAll(),
        api.datasets.list(),
        api.deployments.listAll(),
        api.simulations.list(),
      ])
      setPrompts(promptsData)
      setFolders(foldersData)
      setVariants(variantsData)
      setDatasets(datasetsData)
      setDeployments(deploymentsData)
      setSimulationSuites(simulationSuitesData)
    } catch (error) {
      console.error('Failed to fetch entities:', error)
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  const createRefresh = <T, >(fetcher: () => Promise<T>, setter: (data: T) => void, label: string) => useCallback(async () => {
    if (!isAuthenticated) return
    try {
      setter(await fetcher())
    } catch (error) {
      console.error(`Failed to refresh ${label}:`, error)
    }
  }, [isAuthenticated])

  const refreshPrompts = createRefresh(api.prompts.list, setPrompts, 'prompts')
  const refreshFolders = createRefresh(api.folders.list, setFolders, 'folders')
  const refreshVariants = createRefresh(api.promptVariants.listAll, setVariants, 'variants')
  const refreshDatasets = createRefresh(api.datasets.list, setDatasets, 'datasets')
  const refreshDeployments = createRefresh(api.deployments.listAll, setDeployments, 'deployments')
  const refreshSimulationSuites = createRefresh(api.simulations.list, setSimulationSuites, 'simulationSuites')

  const refreshAll = useCallback(async () => {
    setLoading(true)
    await fetchAll()
  }, [fetchAll])

  return (
    <EntitiesContext.Provider value={{
      prompts,
      folders,
      variants,
      datasets,
      deployments,
      simulationSuites,
      loading,
      refreshPrompts,
      refreshFolders,
      refreshVariants,
      refreshDatasets,
      refreshDeployments,
      refreshSimulationSuites,
      refreshAll,
    }}>
      {children}
    </EntitiesContext.Provider>
  )
}

export function useEntities () {
  const context = useContext(EntitiesContext)
  if (context === undefined) {
    throw new Error('useEntities must be used within an EntitiesProvider')
  }
  return context
}
